import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFundContext from '@/contexts/Fund/useFundContext';
import useQueueAlert from '@/hooks/useQueueAlert';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import { FundInquiryActivityDtoIn } from '@/models/in/FundInquiryActivityDtoIn';

/**
 * Rings for the COORDINATOR when the office answers. Renders nothing.
 *
 * ⚠️ This direction had no alert at all. InquiryQueueAlert is the office's, gated on
 * Admin/Debugger, so a coordinator got a dot in the sidebar that updated every 30 seconds and
 * an email - and nothing while sitting in front of the screen waiting for the answer they
 * just asked for. The chime was only ever half-built.
 *
 * The count is the coordinator's own unread replies, which is a different question from the
 * office's queue depth: it rises on every office reply and clears when they read the thread.
 * Unlike the office side, no timestamp is needed - an office reply always moves this number,
 * because "unread" is defined against the coordinator's own last read.
 *
 * Mounted inside FundProvider: the endpoint is per fund, and there is no coordinator inbox
 * across funds. Switching funds re-arms on that fund's count, which is correct - a coordinator
 * looking at fund A is not waiting on fund B.
 */

/** Same 15s as the office side; the two share nothing else. */
const POLL_INTERVAL = 15000;

export default function CoordinatorReplyAlert() {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const { currentUser } = useAuthContext();
  const fundId = currentFund?.id;
  /*
   * With inquiries off there is no thread for the office to answer, so this stops polling and
   * stops arming - not merely rendering nothing, which it already does.
   */
  const isFeatureOn = useInquiriesFeature();
  const isActive = isFeatureOn && !!fundId && !!currentUser?.token;

  const url = `funds/${fundId}/inquiries/unread-count`;
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data } = useQuery<FundInquiryActivityDtoIn>({
    /*
     * The same key SidebarBadgeProvider uses, so the dot and the chime read one cached value
     * and the queue is polled once. That provider asks for it every 30s; whichever interval
     * fires first refreshes both.
     */
    queryKey: [url],
    queryFn: ({ signal }) => getAuth<FundInquiryActivityDtoIn>(url, headers, undefined, signal),
    enabled: isActive,
    staleTime: 0,
    refetchInterval: POLL_INTERVAL,
    /*
     * ⚠️ בלי זה אין התראה בכלל כשהחלון לא במיקוד - react-query עוצר רענון
     * במרווחים כברירת מחדל, וזה בדיוק המצב שבו ההתראה נחוצה. ראה ההערה
     * המלאה ב-useInquiryQueueCount.
     */
    refetchIntervalInBackground: true,
    /* כמו בצד המשרד: חזרה לחלון דוגמת מיד ולא ממתינה לתקתוק הבא. */
    refetchOnWindowFocus: true,
  });

  const buildNotificationBody = useCallback(
    (count: number) => t('inquiries.alerts.replyNotificationBody', { count }),
    [t],
  );

  useQueueAlert({
    count: data?.count,
    /*
     * ⚠️ זה מה שמצלצל, ולא המונה. "לא נקרא" נמדד מול LastReadAt של הרכז, ופתיחת
     * השרשור **מעדכנת** אותו - כלומר רכז שיושב על הפנייה ומחכה לתשובה מאפס
     * לעצמו את המונה בכל רענון, והתשובה מגיעה כשהמונה עובר 0 ← 1 ← 0 בין שתי
     * דגימות, או לא זז בכלל. חתימת זמן לא נמחקת בקריאה.
     */
    lastUserMessageAt: data ? (data.lastOfficeMessageAt ?? null) : undefined,
    storageScope: 'coordinator',
    // הכותרת בטאב שייכת לצד המשרד; שני כותבים לאותה כותרת מוחקים אחד את השני
    ownsTabTitle: false,
    enabled: isActive,
    notificationTitle: t('inquiries.alerts.replyNotificationTitle'),
    buildNotificationBody,
  });

  return null;
}
